import { useCallback, useEffect, useMemo, useState } from 'react';
import { buildOdpCapacityPredictions } from '../../../utils/capacityPrediction';
import { clearStaffSession, isJwtExpired, resolveBackendBaseUrl } from '../../../utils/runtimeConfig';
import { fetchTickets, mapTicketItem } from '../utils/ticketingApi';
import { normalizeFtthTopologyPayload, normalizeVoucherNasPayload } from '../utils/coreEndpointContract';
import { buildSiteCatalog, resolveCanonicalSiteIdentity } from '../utils/siteIdentity';

const API = `${resolveBackendBaseUrl()}/api`;

const toArray = (value) => (Array.isArray(value) ? value : []);

const normalizeStatus = (value) => String(value || '').trim().toLowerCase();

const hasValidCoordinates = (lat, lon) => {
  const latitude = Number(lat);
  const longitude = Number(lon);
  return Number.isFinite(latitude) && Number.isFinite(longitude) && latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;
};

const buildIncidentSeverity = (status) => {
  const normalized = normalizeStatus(status);
  if (['offline', 'down', 'critical', 'error', 'terminated'].includes(normalized)) return 'critical';
  if (['degraded', 'warning', 'warn', 'maintenance', 'suspended', 'pending'].includes(normalized)) return 'high';
  return 'low';
};

const buildIncidentTone = (severity) => {
  if (severity === 'critical') return 'danger';
  if (severity === 'high') return 'warn';
  return 'info';
};

const buildCustomerIncidents = (customers = [], siteCatalog = null) => {
  const incidents = [];
  customers.forEach((customer) => {
    const status = normalizeStatus(customer?.status || customer?.connection_status || customer?.monitoring_status);
    if (!status || ['online', 'active', 'healthy'].includes(status)) return;

    const severity = buildIncidentSeverity(status);
    const siteIdentity = resolveCanonicalSiteIdentity(customer, siteCatalog);
    incidents.push({
      id: `INC-CUST-${customer.id}`,
      type: 'customer',
      severity,
      tone: buildIncidentTone(severity),
      title: `Gangguan pelanggan ${customer.name || customer.customer_id || customer.id}`,
      status: severity === 'critical' ? 'open' : 'investigating',
      summary: `Status terdeteksi ${status.toUpperCase()}.`,
      suspectedCause: customer?.status_detail?.root_cause || 'Perlu investigasi akses pelanggan.',
      impactedEntities: [
        `customer:${customer.id}`,
        customer?.linked_odp_id ? `odp:${customer.linked_odp_id}` : null,
        customer?.linked_olt_id ? `olt:${customer.linked_olt_id}` : null,
        customer?.router_id ? `nas:${customer.router_id}` : null,
      ].filter(Boolean),
      affectedCustomers: [customer.id],
      createdAt: customer?.status_detail?.last_inform || new Date().toISOString(),
      site_id: siteIdentity.site_id || '',
      site: siteIdentity.site_name || siteIdentity.site_id || '-',
      coordinates: hasValidCoordinates(customer?.latitude, customer?.longitude)
        ? [Number(customer.latitude), Number(customer.longitude)]
        : null,
    });
  });
  return incidents;
};

const buildCapacityIncidents = (odps = [], predictionsByOdpId = {}, siteCatalog = null) => {
  return odps
    .map((odp) => {
      const prediction = predictionsByOdpId[String(odp?.id || '')];
      if (!prediction) return null;
      if (!['critical', 'high'].includes(String(prediction?.risk?.level || ''))) return null;

      const severity = String(prediction.risk.level) === 'critical' ? 'critical' : 'high';
      const siteIdentity = resolveCanonicalSiteIdentity(odp, siteCatalog);
      return {
        id: `INC-ODP-${odp.id}`,
        type: 'capacity',
        severity,
        tone: buildIncidentTone(severity),
        title: `Risiko kapasitas ${odp.name || odp.code || odp.id}`,
        status: 'open',
        summary: `${prediction.used}/${prediction.capacity} terpakai (${prediction.occupancyPercent}%).`,
        suspectedCause: 'Beban pelanggan mendekati limit ODP.',
        impactedEntities: [
          `odp:${odp.id}`,
          odp?.olt_id ? `olt:${odp.olt_id}` : null,
        ].filter(Boolean),
        affectedCustomers: [],
        createdAt: new Date().toISOString(),
        site_id: siteIdentity.site_id || '',
        site: siteIdentity.site_name || siteIdentity.site_id || '-',
        coordinates: hasValidCoordinates(odp?.latitude, odp?.longitude)
          ? [Number(odp.latitude), Number(odp.longitude)]
          : null,
      };
    })
    .filter(Boolean);
};

const buildSyntheticTicketsFromIncidents = (incidents = []) => {
  return incidents.map((incident, index) => ({
    id: `TCK-${7600 + index}`,
    ticketCode: `TCK-SYN-${7600 + index}`,
    incidentId: incident.id,
    status: incident.severity === 'critical' ? 'open' : 'investigating',
    priority: incident.severity === 'critical' ? 'high' : 'medium',
    assignedTo: incident.severity === 'critical' ? 'NOC-Shift-A' : 'NOC-Shift-B',
    summary: incident.title,
    sourceType: 'incident',
    sourceId: incident.id,
    actionLog: [],
  }));
};

const buildTimelineEvents = (incidents = []) => {
  return incidents.slice(0, 20).map((incident, index) => ({
    id: `EV-${index + 1}`,
    incidentId: incident.id,
    title: incident.title,
    detail: incident.summary,
    timestamp: incident.createdAt,
    tone: incident.tone,
    source: incident.type === 'capacity' ? 'Capacity Engine' : 'Monitoring Engine',
  }));
};

const fetchJsonWithTimeout = async (url, options = {}, timeoutMs = 8000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.detail || `Request gagal (${response.status})`);
    }
    return payload;
  } finally {
    clearTimeout(timeoutId);
  }
};

export function useOperationalDataset(navigate) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState([]);
  const [odps, setOdps] = useState([]);
  const [olts, setOlts] = useState([]);
  const [nasRouters, setNasRouters] = useState([]);
  const [sites, setSites] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [lastUpdated, setLastUpdated] = useState('');

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_token');
    if (!token || isJwtExpired(token)) {
      clearStaffSession();
      if (typeof navigate === 'function') navigate('/dadu/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const refresh = useCallback(async () => {
    const headers = getHeaders();
    if (!headers) return;
    setLoading(true);
    setError('');
    try {
      const [topologyRes, statusRes, nasRes, sitesRes, ticketsRes] = await Promise.allSettled([
        fetchJsonWithTimeout(`${API}/ftth/topology`, { headers }, 10000),
        fetchJsonWithTimeout(`${API}/genieacs/customer-status`, { headers }, 6000),
        fetchJsonWithTimeout(`${API}/voucher/nas`, { headers }, 8000),
        fetchJsonWithTimeout(`${API}/sites`, { headers }, 6000),
        fetchTickets({ headers, params: { limit: 250 } }),
      ]);

      if (topologyRes.status !== 'fulfilled') throw new Error('Gagal memuat data topology.');

      const topology = normalizeFtthTopologyPayload(topologyRes.value || {});
      const statusPayload = statusRes.status === 'fulfilled' ? (statusRes.value || {}) : {};
      const nasPayload = normalizeVoucherNasPayload(nasRes.status === 'fulfilled' ? (nasRes.value || {}) : {});
      const sitePayload = sitesRes.status === 'fulfilled' ? (sitesRes.value || []) : [];

      const statusByCustomerId = {};
      toArray(statusPayload?.statuses).forEach((item) => {
        if (item?.customer_id) statusByCustomerId[String(item.customer_id)] = item;
      });

      const normalizedCustomers = toArray(topology?.customers).map((customer) => {
        const id = String(customer?.id || '');
        const statusDetail = statusByCustomerId[id] || null;
        const mergedStatus = statusDetail?.connection_status || customer?.connection_status || customer?.status || 'unknown';
        return {
          ...customer,
          status: mergedStatus,
          status_detail: statusDetail,
          linked_odp_id: customer?.linked_odp_id || customer?.odp_id || null,
          linked_olt_id: customer?.linked_olt_id || customer?.olt_id || null,
          router_id: customer?.router_id || customer?.linked_router_id || null,
        };
      });

      setCustomers(normalizedCustomers);
      setOdps(toArray(topology.odps));
      setOlts(toArray(topology.olts));
      setNasRouters(toArray(nasPayload.items));
      setSites(toArray(sitePayload));
      setTickets(ticketsRes.status === 'fulfilled' ? toArray(ticketsRes.value?.items).map(mapTicketItem) : []);
      setLastUpdated(statusPayload?.updated_at || new Date().toISOString());
    } catch (fetchError) {
      setError(fetchError?.message || 'Gagal memuat data operasional.');
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const capacityPredictions = useMemo(() => {
    const odpPredictions = buildOdpCapacityPredictions({
      odps,
      customers,
      resolveCustomerOdpId: (customer) => customer?.linked_odp_id || customer?.odp_id,
    });
    const byOdpId = {};
    odpPredictions.forEach((item) => { byOdpId[String(item.id)] = item; });
    return { list: odpPredictions, byOdpId };
  }, [customers, odps]);

  const incidents = useMemo(() => {
    const siteCatalog = buildSiteCatalog(sites);
    const customerIncidents = buildCustomerIncidents(customers, siteCatalog);
    const capacityIncidents = buildCapacityIncidents(odps, capacityPredictions.byOdpId, siteCatalog);
    return [...customerIncidents, ...capacityIncidents].sort((a, b) => {
      const rank = { critical: 3, high: 2, low: 1 };
      return (rank[b.severity] || 0) - (rank[a.severity] || 0);
    });
  }, [customers, odps, sites, capacityPredictions.byOdpId]);

  const effectiveTickets = useMemo(() => (
    tickets.length ? tickets : buildSyntheticTicketsFromIncidents(incidents)
  ), [tickets, incidents]);

  const timelineEvents = useMemo(() => buildTimelineEvents(incidents), [incidents]);

  return {
    loading,
    error,
    refresh,
    lastUpdated,
    customers,
    odps,
    olts,
    nasRouters,
    sites,
    capacityPredictions,
    incidents,
    tickets: effectiveTickets,
    timelineEvents,
  };
}
